import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import type { CreateViewRequest } from "../ViewRecord";

import { viewQueryKeys } from "./viewQueryKeys";

/**
 * Copy a View. Needs only permission to read the source, since it writes something new.
 *
 * `request` is optional: omit it to take the source's name and definition, or supply one to
 * rename the copy at the same time.
 */
export function useDuplicateView() {
  const { viewService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { viewId: string; request?: CreateViewRequest }) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }
      return viewService.duplicateView(
        params.viewId,
        currentOrganization.org_id,
        { request: params.request },
      );
    },
    // Captured when the mutation starts, not when it settles: `onSuccess`
    // reads whatever org is current at completion, and a request that spans an
    // org switch would otherwise invalidate the org the user moved to.
    onMutate: () => ({ orgId: currentOrganization?.org_id }),
    onSuccess: (_, __, { orgId }) => {
      void queryClient.invalidateQueries({
        queryKey: viewQueryKeys.lists(orgId),
      });
    },
  });
}
